import { Link } from 'react-router-dom';

interface VideoCardProps {
  id: string;
  title: string;
  thumbnail: string;
  authorName: string;
  authorAvatar: string;
  views: number;
  likes: number;
  duration: string;
  categoryName: string;
  categorySlug: string;
}

function formatNumber(num: number): string {
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + '万';
  }
  return num.toString();
}

export default function VideoCard({
  id,
  title,
  thumbnail,
  authorName,
  authorAvatar,
  views,
  likes,
  duration,
  categoryName,
  categorySlug,
}: VideoCardProps) {
  return (
    <Link
      to={`/article/${id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 transition-all duration-300 hover:border-[oklch(var(--primary-500)/0.3)]"
    >
      {/* Thumbnail */}
      <div className="relative w-full aspect-video overflow-hidden">
        <img
          src={thumbnail}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {/* Duration Badge */}
        <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded whitespace-nowrap">
          {duration}
        </span>
        {/* Play Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <i className="ri-play-fill text-foreground-900 text-xl ml-0.5"></i>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-[oklch(var(--primary-500))] transition-colors">
          {title}
        </h3>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={authorAvatar}
              alt={authorName}
              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
              loading="lazy"
            />
            <span className="text-xs text-foreground-500 truncate">{authorName}</span>
          </div>
          <Link
            to={`/category/${categorySlug}`}
            className="text-xs text-[oklch(var(--primary-500))] bg-[oklch(var(--primary-500)/0.08)] px-2 py-0.5 rounded-full whitespace-nowrap hover:bg-[oklch(var(--primary-500)/0.15)] transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {categoryName}
          </Link>
        </div>

        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatNumber(views)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-heart-line"></i>
            {formatNumber(likes)}
          </span>
        </div>
      </div>
    </Link>
  );
}